// pages/Editor/components/AddStepButton/AddStepButton.tsx
import { useState } from 'react';
import {
  ActionIcon,
  Box,
  Center,
  Collapse,
  Group,
  Popover,
  Stack,
  Text,
  UnstyledButton,
  Button,
} from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { HiMiniChevronDown, HiMiniChevronRight, HiOutlinePlus } from 'react-icons/hi2';
import { STEP_TYPE_PICKER_CATEGORIES, STEP_TYPE_UI_CONFIG } from '@/pages/Editor/types';
import StepCardIcon from '@/components/StepCard/StepCardIcon.tsx';
import AppIcon from '@/components/AppIcon';
import useAddStep from '@/hooks/api/steps/useAddStep';

import type { SiblingParentOption } from '@/pages/Editor/components/tree/types';
import type { AutomationStep } from '@/types/automationSteps';
import classes from './AddStepButton.module.css';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { getActiveConditionId } from '@/utils/stepUtils.ts';
import { StepType, type AddableStepType } from '@/types/automationFields.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';

type Props = {
  addSiblingParents: SiblingParentOption[]; // all valid "add-after" targets for this row
  contextStep?: AutomationStep; // the step this button sits under
  alwaysShow?: boolean;
  buttonLabel?: string;
  activeConditionIds?: Record<string, string>;
};

type Stage = 'none' | 'target' | 'types';

function targetLabel(opt: SiblingParentOption, context?: AutomationStep) {
  // Insert INSIDE current step
  if (context && opt.parentStep?.step_id === context.step_id && opt.insertIndex === 0) {
    return `Inside “${context.name ?? 'This step'}”`;
  }

  // Top level
  // if (!opt.parentStep) return `Top level (After “${opt.afterStep?.name ?? 'last step'}”)`;
  if (!opt.parentStep) return `Top level`;

  // If we know what we’re inserting after, use that
  if (opt.afterStep) return `After “${opt.afterStep.name ?? 'This step'}”`;

  // Otherwise fall back to the container name
  return `Inside “${opt.parentStep.name ?? 'parent'}”`;
}

export default function AddStepButton({
  addSiblingParents,
  contextStep,
  alwaysShow = false,
  buttonLabel,
  activeConditionIds = {},
}: Props) {
  const { hovered, ref: hoveredRef } = useHover();
  const { mutate: addStep, isPending } = useAddStep();
  const { target } = useActiveCompositionTarget();

  const [stage, setStage] = useState<Stage>('none');
  const [selected, setSelected] = useState<SiblingParentOption | null>(null);
  const [lessCommonOpened, setLessCommonOpened] = useState(false);

  const opened = stage !== 'none';
  const show = alwaysShow || hovered || opened || isPending;

  function onPrimaryClick() {
    if (addSiblingParents.length <= 1) {
      setSelected(addSiblingParents[0] ?? { parentStep: undefined, insertIndex: 0 });
      setStage('types');
    } else {
      setSelected(null);
      setStage('target');
    }
  }

  function onPickTarget(opt: SiblingParentOption) {
    setSelected(opt);
    setStage('types');
  }

  function onClose() {
    setStage('none');
    setSelected(null);
    setLessCommonOpened(false);
  }

  function renderStepType(type: AddableStepType) {
    const cfg = STEP_TYPE_UI_CONFIG.find((c) => c.key === type);
    if (!cfg) return null;
    // A local-module call needs typed bindings and compiler-backed mutation.
    // It is created from the Local Modules workspace, not this generic picker.
    if (
      cfg.key === StepType.LocalSubflowCall ||
      (target.scope_kind === 'local_subflow' && cfg.key === StepType.Trigger)
    ) return null;
    return (
      <Group
        key={cfg.key}
        gap="xs"
        onClick={() => {
          const conditionId = getActiveConditionId(selected!.parentStep, activeConditionIds);

          addStep({
            step_type: cfg.key,
            step_id: selected!.parentStep?.step_id, // undefined => root
            condition_id: conditionId,
            index: selected!.insertIndex,
          });
          onClose();
        }}
        className={classes.item}
        wrap="nowrap"
        style={{ cursor: 'pointer', alignItems: 'center' }}
      >
        <StepCardIcon
          Icon={cfg.icon}
          CustomIcon={cfg.svg}
          bg={cfg.iconBackgroundColor}
          iconColor={cfg.iconColor}
          size={30}
          style={{ flexShrink: 0, borderRadius: 5 }}
        />
        <Stack gap={0}>
          <Text className={classes.label}>{cfg.label}</Text>
          <Text className={classes.description} style={{ fontSize: 12 }}>
            {cfg.description}
          </Text>
        </Stack>
      </Group>
    );
  }

  return (
    <Stack
      ref={hoveredRef}
      w="100%"
      style={{ position: 'relative', opacity: show ? 1 : 0, transition: 'all 250ms ease' }}
    >
      <Box style={{ position: 'absolute', left: 0, right: 0, marginTop: 9 }}>
        <Popover
          opened={opened && stage === 'target'}
          onChange={(o) => (!o ? onClose() : null)}
          position="bottom"
          shadow="md"
        >
          <Popover.Target>
            <Center>
              {buttonLabel ? (
                <Button
                  variant="filled"
                  color="brandBlue.7"
                  c="white"
                  leftSection={<HiOutlinePlus size={16} />}
                  onClick={onPrimaryClick}
                  loading={isPending}
                >
                  {buttonLabel}
                </Button>
              ) : (
                <ActionIcon
                  size="sm"
                  variant="filled"
                  onClick={onPrimaryClick}
                  loading={isPending}
                  classNames={{ root: classes.button }}
                  aria-label="Add step"
                  mt={alwaysShow ? 'sm' : 0}
                >
                  <AppIcon Icon={HiOutlinePlus} size={16} iconProps={{ strokeWidth: 2 }} />
                </ActionIcon>
              )}
            </Center>
          </Popover.Target>

          <Popover.Dropdown p="xs">
            {stage === 'target' && (
              <Stack gap="4px" maw={520}>
                {addSiblingParents.map((opt) => {
                  const key = `${opt.parentStep?.step_id ?? 'root'}:${opt.insertIndex}`;
                  return (
                    <Group
                      key={key}
                      gap="xs"
                      onClick={() => onPickTarget(opt)}
                      className={classes.item}
                      wrap="nowrap"
                    >
                      <Text className={classes.label}>{targetLabel(opt, contextStep)}</Text>
                    </Group>
                  );
                })}
              </Stack>
            )}
          </Popover.Dropdown>
        </Popover>
      </Box>

      <StyledModal
        opened={stage === 'types' && !!selected}
        onClose={onClose}
        title="Add a step"
        titleFontWeight={600}
        scrollType={'body'}
      >
        <Stack gap="md" mx={'-10px'} mt={'2px'}>
          {STEP_TYPE_PICKER_CATEGORIES.map(({ title, types, collapsedByDefault }) => {
            if (collapsedByDefault) {
              const Icon = lessCommonOpened ? HiMiniChevronDown : HiMiniChevronRight;
              return (
                <Stack key={title} gap="2px">
                  <UnstyledButton
                    className={classes.collapseHeader}
                    aria-expanded={lessCommonOpened}
                    onClick={() => setLessCommonOpened((opened) => !opened)}
                  >
                    <Group gap="4px" wrap="nowrap">
                      <AppIcon Icon={Icon} size={14} color="#757575" />
                      <Text c={'dimmed'} size={'12px'}>
                        {title.toUpperCase()}
                      </Text>
                    </Group>
                  </UnstyledButton>
                  <Collapse expanded={lessCommonOpened}>
                    <Stack gap="2px">{types.map(renderStepType)}</Stack>
                  </Collapse>
                </Stack>
              );
            }

            return (
              <Stack key={title} gap="2px">
                <Text c={'dimmed'} size={'12px'} mb={'6px'} pl={'8px'}>
                  {title.toUpperCase()}
                </Text>
                {types.map(renderStepType)}
              </Stack>
            );
          })}
        </Stack>
      </StyledModal>
    </Stack>
  );
}
